import React, { useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router'
import { yupResolver } from '@hookform/resolvers/yup'
import { NUMBER_OF_INSTALLMENTS_OPTIONS } from '~/utils/selectOptions'
import { CreateAccountFormValues, useCreateAccountFormStore } from '~/store/createAccountForm'
import { OnboardingFormLayout, HfCurrencyInput, HfRadioGroup, HfTextInput } from '~/components'
import * as yup from 'yup'
import { OnboardingWizardActions, trackEvent } from '~/service/analytics'

const mapNumberOfInstallmentsToEvent = (numberOfInstallments: string): OnboardingWizardActions => {
  switch (numberOfInstallments) {
    case '12':
      return OnboardingWizardActions.NUMBER_OF_INSTALLMENTS_12
    case '15':
      return OnboardingWizardActions.NUMBER_OF_INSTALLMENTS_15
    case '18':
      return OnboardingWizardActions.NUMBER_OF_INSTALLMENTS_18
    case '21':
      return OnboardingWizardActions.NUMBER_OF_INSTALLMENTS_21
    case '24':
      return OnboardingWizardActions.NUMBER_OF_INSTALLMENTS_24
    default:
      return OnboardingWizardActions.NUMBER_OF_INSTALLMENTS_12
  }
}

const validationSchema = yup.object().shape({
  requestedAmountCents: yup.string().required('Campo obrigatório')
    .test('minValue',
      'O valor solicitado deve ser no mínimo R$20.000,00',
      value => Number(value) >= 20000)
    .test('maxValue',
      'O valor solicitado deve ser no máximo R$50.000,00',
      value => Number(value) <= 50000),
  numberOfInstallments: yup.string().required('Campo obrigatório'),
  loanMotive: yup.string().required('Campo obrigatório')
})

export const LoanGeneralData: React.FC = () => {
  const { createAccountFormState, updateForm } = useCreateAccountFormStore()
  const { form } = createAccountFormState
  const history = useHistory()

  useEffect(() => {
    if (form.cnpj === '') {
      history.push('/register/company-info')
    }
  }, [form])

  const { handleSubmit, control, formState: { errors } } = useForm<CreateAccountFormValues>({
    defaultValues: form,
    resolver: yupResolver(validationSchema)
  })

  const trackEventsOnSubmit = useCallback((numberOfInstallmets: string) => {
    const mappedEvent = mapNumberOfInstallmentsToEvent(numberOfInstallmets)
    trackEvent(mappedEvent)
    trackEvent(OnboardingWizardActions.ON_LOAN_DATA_NEXT_CLICK)
  }, [])

  const onSubmit = useCallback((data: CreateAccountFormValues) => {
    trackEventsOnSubmit(data.numberOfInstallments)
    updateForm(data)
    history.push('/register/create-account')
  }, [trackEventsOnSubmit, updateForm, history])

  const onBackClick = useCallback(() => {
    history.goBack()
  }, [history])

  return (
    <OnboardingFormLayout
      title='De quanto sua empresa precisa?'
      subtitle='Escolha as condições do seu empréstimo.'
      canContinue
      onNextClick={handleSubmit(onSubmit)}
      onBackClick={onBackClick}
    >
      <HfCurrencyInput
        control={control}
        name='requestedAmountCents'
        label='Valor solicitado'
        error={errors.requestedAmountCents?.message}
        errorMessage={errors.requestedAmountCents?.message}
        subtitle='Valores de R$ 20.000 até R$ 50.000'
        placeholder='R$ 20.000,00'
      />

      <HfRadioGroup
        row
        outsideLabel
        control={control}
        label='Nº de parcelas'
        name='numberOfInstallments'
        defaultValue={form.numberOfInstallments}
        options={NUMBER_OF_INSTALLMENTS_OPTIONS}
      />

      <HfTextInput
        label='Motivo do empréstimo'
        multiline
        rows={5}
        rowsMax={10}
        control={control}
        name='loanMotive'
        error={errors.loanMotive?.message}
        errorMessage={errors.loanMotive?.message}
        placeholder='Preciso de um empréstimo para...'
      />
    </OnboardingFormLayout>
  )
}
